import { Pagination } from 'antd';
import clsx from 'clsx';
import React, { FC, memo, ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Hooks, Row, useExpanded, useRowSelect, useTable } from 'react-table';
import { useSticky } from 'react-table-sticky';
import FnBodyRows from './components/FnBodyRows';
import FnGroupRows from './components/FnGroupRows';
import {
  DivContainer,
  DivFnLoader,
  DivFnTable,
  DivPaginationBlock,
  DivPaginationWrapper,
  DivTableWrapper
} from './styles';
import { TSharedRowProps, TTableColumn } from './types';
import { useContainerDimensions } from './useContainerDimensions';

type TProps = TSharedRowProps & {
  loading?: boolean;
  columns: TTableColumn<any>[];
  data: any[];
  className?: string;
  tableClassName?: string;
  customExpandedKey?: string;
  height?: string;
  leftHeaderBlock?: ReactElement;
  headerBlock?: ReactElement;
  footerBlock?: ReactElement;
  rowsPerPage?: number;
  rowCount?: number;
  currentPage?: number;
  expandedRowOffset?: number;
  renderRowSubComponent?: (row: Row<any>) => ReactNode;
  stickyHead?: boolean;
  onChangePage?: (page: number) => void;
  customHooks?: (hooks: Hooks) => void;
  fixedWidthColumns?: boolean;
  customGroupRows?: boolean;
  customGroupRowsCondition?: (row: Row<any>) => any;
  renderGroupRowSubComponent?: (row: Row<any>[]) => ReactNode;
  filtersRow?: ReactElement;
  filtersRowColspan?: number;
  minHeight?: number;
};

const FnDataTable: FC<TProps> = ({
  loading,
  columns,
  data,
  rowsPerPage,
  rowCount,
  currentPage,
  leftHeaderBlock = null,
  expandedRowOffset = 0,
  renderRowSubComponent = null,
  onChangePage = null,
  onCellUpdate = null,
  onRowSelect = null,
  onRowExpand = null,
  onCellClick = null,
  onHeaderRowSelect = null,
  height = '100%',
  className = '',
  tableClassName = '',
  customHooks = null,
  selectedRows = {},
  customExpandedRows = {},
  customExpandedKey = 'id',
  headerBlock = null,
  footerBlock = null,
  stickyHead = false,
  fixedWidthColumns = false,
  customData = {},
  customGroupRows = false,
  renderGroupRowSubComponent = null,
  customGroupRowsCondition = null,
  filtersRow = null,
  filtersRowColspan = null,
  minHeight = null
}) => {
  const { getTableProps, getTableBodyProps, visibleColumns, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
      // @ts-ignore
      onCellUpdate,
      onCellClick,
      onRowSelect,
      selectedRows,
      customData,
      onRowExpand,
      customExpandedRows,
      onHeaderRowSelect
    },
    useExpanded,
    useSticky,
    useRowSelect,
    (hooks) => {
      if (customHooks) {
        customHooks(hooks);
      }
    }
  );
  const tableWrapperRef = useRef(null);
  const tableRef = useRef(null);
  const tableDimensions = useContainerDimensions(tableRef, data, columns);
  const tableWrapperDimensions = useContainerDimensions(tableWrapperRef, data, columns);
  const [groupedRows, setGroupedRows] = useState<any[]>([]);

  useEffect(() => {
    if (!customGroupRows) {
      return;
    }

    const groups = new Map();
    const result = [];
    rows.forEach((row) => {
      // @ts-ignore
      const id = customGroupRowsCondition(row);

      if (groups.has(id)) {
        const saved = groups.get(id);
        saved.push(row);
        groups.set(id, saved);
      } else {
        groups.set(id, [row]);
      }
    });

    // @ts-ignore
    for (const [key, value] of groups.entries()) {
      result.push(value);
    }

    setGroupedRows(result);
  }, [rows, customGroupRows, customGroupRowsCondition]);

  const customFiltersRow = useMemo(() => {
    return filtersRow && filtersRowColspan ? (
      <tr className="filters-row">
        <td colSpan={filtersRowColspan}>{filtersRow}</td>
      </tr>
    ) : null;
  }, [filtersRow, filtersRowColspan]);

  return (
    <DivContainer className={clsx('fn-table', className)}>
      {loading && (
        <DivFnLoader>
          <div className="loader" />
        </DivFnLoader>
      )}

      {leftHeaderBlock || currentPage ? (
        <DivPaginationWrapper>
          <div>{leftHeaderBlock}</div>
          <DivPaginationBlock>
            {currentPage && (
              <Pagination
                size="small"
                total={rowCount}
                pageSize={rowsPerPage}
                current={currentPage}
                hideOnSinglePage={true}
                onChange={onChangePage}
                showSizeChanger={false}
                showQuickJumper
              />
            )}
          </DivPaginationBlock>
        </DivPaginationWrapper>
      ) : (
        <div></div>
      )}

      <div>{headerBlock}</div>

      <DivTableWrapper ref={tableWrapperRef} className="fn-table-wrapper styled-scroll bold-scroll" style={{ height, minHeight }}>
        <DivFnTable {...getTableProps()} className={clsx('table', tableClassName, { 'sticky-head': stickyHead })}>
          <thead ref={tableRef}>
            {headerGroups.map((headerGroup, i) => (
              <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, j) => {
                  // @ts-ignore
                  let style = column.style;

                  if (
                    fixedWidthColumns &&
                    columns.length - 1 === j &&
                    tableWrapperDimensions.width === tableDimensions.width
                  ) {
                    const currentStyle = { ...style };
                    const width = currentStyle?.width;
                    const minWidth = width ? currentStyle?.width : '100px';

                    delete currentStyle.width;

                    style = {
                      ...currentStyle,
                      minWidth
                    };
                  }

                  return (
                    // @ts-ignore
                    <th key={j} {...column.getHeaderProps([{ className: column.className, style }])}>
                      {column.render('Header')}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {customFiltersRow}
            {customGroupRows ? (
              <FnGroupRows
                rows={groupedRows}
                renderRowSubComponent={renderRowSubComponent}
                customExpandedRows={customExpandedRows}
                expandedRowOffset={expandedRowOffset}
                visibleColumns={visibleColumns}
                prepareRow={prepareRow}
                customExpandedKey={customExpandedKey}
                renderGroupRowSubComponent={renderGroupRowSubComponent}
              />
            ) : (
              <FnBodyRows
                prepareRow={prepareRow}
                renderRowSubComponent={renderRowSubComponent}
                customExpandedRows={customExpandedRows}
                expandedRowOffset={expandedRowOffset}
                visibleColumns={visibleColumns}
                rows={rows}
                customExpandedKey={customExpandedKey}
              />
            )}
          </tbody>
        </DivFnTable>
      </DivTableWrapper>

      <div>{footerBlock}</div>
    </DivContainer>
  );
};

export default memo(FnDataTable);
